import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/admin/ui";
import { RecordFiles } from "@/components/admin/RecordFiles";

const DISPOSITIONS = ["Nolle Prossed", "Dismissed", "Probation before Judgement", "Guilty", "Not Guilty", "Dismissed with Prejudice", "Dismissed without Prejudice", "First Offender Program", "Pending"];
const LEVELS = ["Violation", "Misdemeanor", "Felony", "Traffic Citation", "Traffic Violation", "Traffic Misdemeanor", "Traffic Felony", "Citation"];
const SOURCES = ["Delaware Courts", "Delaware Sex Offender Registry", "Delaware Adult Abuse Registry", "US DOJ Sex Offender Registry (NSOPW)", "Out-of-state court"];
const blankCount = () => ({ arrest_charge: "", disposed_charge: "", disposition: "", offense_level: "", sentence: "", pglio: false });
const blankRecord = (p: any) => ({ first_name: p.first_name || "", middle_name: p.middle_name || "", last_name: p.last_name || "", dob: "", dl_number: "",
  case_number: "", arrest_date: "", file_date: "", disposition_date: "", court: "", county: "", source: "Delaware Courts", counts: [blankCount()] });

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="field-label">{label}</span>{children}</label>;
}

function Choice({ value, options, onChange, testId }: { value: string; options: string[]; onChange: (v: string) => void; testId: string }) {
  const other = value !== "" && !options.includes(value);
  const [typing, setTyping] = useState(other);
  return (
    <div className="space-y-2">
      <select className="field-input" value={typing ? "__other" : value} data-testid={testId}
        onChange={(e) => { const v = e.target.value; setTyping(v === "__other"); onChange(v === "__other" ? "" : v); }}>
        <option value="">Select…</option>{options.map((o) => <option key={o}>{o}</option>)}<option value="__other">Other (type in)</option>
      </select>
      {typing && <input className="field-input" placeholder="Type it in" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testId}-other`} />}
    </div>
  );
}

function CountRow({ ct, i, set, remove, duplicate, pre }: { ct: any; i: number; set: (k: string, v: any) => void; remove: () => void; duplicate: () => void; pre: string }) {
  const inp = (k: string, v = ct[k]) => <input className="field-input" value={v || ""} onChange={(e) => set(k, e.target.value)} data-testid={`${pre}-${k}`} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-[#FCFBF8] p-4" data-testid={pre}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-sm font-bold text-[#1B365D]">Count {i + 1}</span>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => set("pglio", !ct.pglio)} data-testid={`${pre}-pglio`} aria-pressed={!!ct.pglio}
            title="Pled guilty to a lesser included offense: also show the original arrest charge on the report"
            className={`rounded-full border px-3 py-1 text-xs font-bold tracking-wide transition-colors ${ct.pglio ? "border-[#1B365D] bg-[#1B365D] text-white" : "border-slate-300 text-[#475569] hover:border-[#1B365D]"}`}>PGLIO</button>
          <button type="button" className="text-xs font-semibold text-[#1B365D] hover:underline" onClick={duplicate} data-testid={`${pre}-duplicate`}>Duplicate charge</button>
          <button type="button" className="text-xs font-semibold text-[#B91C1C]" onClick={remove} data-testid={`${pre}-remove`}>Remove count</button>
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {ct.pglio ? (
          <>
            <F label="Original arrest charge">{inp("arrest_charge")}</F>
            <F label="Disposed charge (pled down to)">{inp("disposed_charge")}</F>
          </>
        ) : (
          <div className="sm:col-span-2"><F label="Charge (as disposed)">{inp("disposed_charge", ct.disposed_charge || ct.arrest_charge)}</F></div>
        )}
        <F label="Disposition"><Choice value={ct.disposition} options={DISPOSITIONS} onChange={(v) => set("disposition", v)} testId={`${pre}-disposition`} /></F>
        <F label="Offense level"><Choice value={ct.offense_level} options={LEVELS} onChange={(v) => set("offense_level", v)} testId={`${pre}-offense_level`} /></F>
        <div className="sm:col-span-2"><F label="Sentence">{inp("sentence")}</F></div>
      </div>
    </div>
  );
}

function RecordForm({ r, i, onChange, onRemove, files }: { r: any; i: number; onChange: (r: any) => void; onRemove: () => void; files: React.ReactNode }) {
  const pre = `record-${i}`;
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...r, [k]: e.target.value });
  const inp = (k: string, type = "text") => <input type={type} className="field-input" value={r[k] || ""} onChange={set(k)} data-testid={`${pre}-${k}`} />;
  const setCount = (j: number, k: string, v: any) => onChange({ ...r, counts: r.counts.map((c: any, x: number) => (x === j ? { ...c, [k]: v } : c)) });
  return (
    <div className="space-y-4 rounded-2xl border-l-4 border-[#B91C1C] bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid={pre}>
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-[#1A2536]">Case {i + 1}</h4>
        <button type="button" className="flex items-center gap-1 text-xs font-semibold text-[#B91C1C]" onClick={onRemove} data-testid={`${pre}-remove`}><Trash2 className="h-3.5 w-3.5" /> Remove case</button>
      </div>
      <F label="Record source (shown as the case header on the report)"><Choice value={r.source || ""} options={SOURCES} onChange={(v) => onChange({ ...r, source: v })} testId={`${pre}-source`} /></F>
      <div className="grid gap-3 sm:grid-cols-3">
        <F label="Name on file (first)">{inp("first_name")}</F>
        <F label="Middle name on file">{inp("middle_name")}</F>
        <F label="Last name on file">{inp("last_name")}</F>
        <F label="DOB">{inp("dob", "date")}</F>
        <F label="Driver license">{inp("dl_number")}</F>
        <F label="Case number">{inp("case_number")}</F>
        <F label="Arrest date">{inp("arrest_date", "date")}</F>
        <F label="File date">{inp("file_date", "date")}</F>
        <F label="Disposition date">{inp("disposition_date", "date")}</F>
        <div className="sm:col-span-2"><F label="Court">{inp("court")}</F></div>
        <F label="County">{inp("county")}</F>
      </div>
      <div className="space-y-3">
        {r.counts.map((ct: any, j: number) => (
          <CountRow key={j} ct={ct} i={j} pre={`${pre}-count-${j}`} set={(k, v) => setCount(j, k, v)} remove={() => onChange({ ...r, counts: r.counts.filter((_: any, x: number) => x !== j) })}
            duplicate={() => onChange({ ...r, counts: [...r.counts.slice(0, j + 1), { ...ct }, ...r.counts.slice(j + 1)] })} />
        ))}
        <button type="button" className="btn-secondary btn-sm" onClick={() => onChange({ ...r, counts: [...r.counts, blankCount()] })} data-testid={`${pre}-add-count`}><Plus className="h-4 w-4" /> Add another count</button>
      </div>
      {files}
    </div>
  );
}

export default function CaseRecords({ c, locked, onCase }: { c: any; locked: boolean; onCase: (data: any) => void }) {
  const [records, setRecords] = useState<any[]>(c.records || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRecords(c.records || []); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/cases/${c.id}/records`, { records });
      setRecords(data.records);
      toast.success("Records saved. They'll be included in the final report.");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Card title={`Records found (${records.length})`} testId="case-records-card">
      <div className="space-y-4 p-5">
        {records.map((r, i) => (
          <fieldset key={i} disabled={locked}>
            <RecordForm r={r} i={i} onChange={(nr) => setRecords(records.map((x, k) => (k === i ? nr : x)))} onRemove={() => setRecords(records.filter((_, k) => k !== i))}
              files={<RecordFiles c={c} record={r} locked={locked} onCase={onCase} pre={`record-${i}`} />} />
          </fieldset>
        ))}
        {!records.length && <p className="text-sm text-[#64748B]">No court records added. If a search finds a record, add each case here.</p>}
        {!locked && (
          <div className="flex flex-wrap gap-3">
            <button type="button" className="btn-secondary btn-sm" onClick={() => setRecords([...records, blankRecord(c.data?.personal || {})])} data-testid="add-record-button"><Plus className="h-4 w-4" /> Add case</button>
            <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={save} data-testid="save-records-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save records</button>
          </div>
        )}
      </div>
    </Card>
  );
}
